// ─── Shared wind field ─────────────────────────────────────────────────────
// One 2D simplex-noise + gust model, sampled by every wind-driven asset so they
// all breathe to the SAME wind. Used by BlowingPetals (drifting leaves) and
// PaperSun (ray sway): same noise field, same gust timing, same direction at a
// given windStrength via makeWindConfig().
//
// Kept as plain pure functions (no React) so the field is module-global: the
// permutation table below is shuffled once per page load and shared by all
// importers, which is what makes the coupling real rather than cosmetic.

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;
const grad3 = [[1, 1], [-1, 1], [1, -1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]];
const perm = new Uint8Array(512);
const permMod8 = new Uint8Array(512);
(() => {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    permMod8[i] = perm[i] % 8;
  }
})();

export function simplex2(x, y) {
  const s = (x + y) * F2;
  const i = Math.floor(x + s), j = Math.floor(y + s);
  const t = (i + j) * G2;
  const x0 = x - (i - t), y0 = y - (j - t);
  const i1 = x0 > y0 ? 1 : 0, j1 = x0 > y0 ? 0 : 1;
  const x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
  const x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
  const ii = i & 255, jj = j & 255;
  let n0 = 0, n1 = 0, n2 = 0;
  let t0 = 0.5 - x0 * x0 - y0 * y0;
  if (t0 > 0) { t0 *= t0; const g = grad3[permMod8[ii + perm[jj]]]; n0 = t0 * t0 * (g[0] * x0 + g[1] * y0); }
  let t1 = 0.5 - x1 * x1 - y1 * y1;
  if (t1 > 0) { t1 *= t1; const g = grad3[permMod8[ii + i1 + perm[jj + j1]]]; n1 = t1 * t1 * (g[0] * x1 + g[1] * y1); }
  let t2 = 0.5 - x2 * x2 - y2 * y2;
  if (t2 > 0) { t2 *= t2; const g = grad3[permMod8[ii + 1 + perm[jj + 1]]]; n2 = t2 * t2 * (g[0] * x2 + g[1] * y2); }
  return 70 * (n0 + n1 + n2);
}

export function fbm(x, y, octaves = 4) {
  let val = 0, amp = 1, freq = 1, max = 0;
  for (let i = 0; i < octaves; i++) {
    val += simplex2(x * freq, y * freq) * amp;
    max += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return val / max;
}

// Reusable output object to avoid per-frame array allocation. Safe to share:
// the rAF loops never interleave, so each caller reads the result before the
// next call overwrites it.
const _windOut = { x: 0, y: 0 };

export function sampleWind(x, y, time, config, containerWidth) {
  // Scale noise relative to container size so wind patterns look consistent at any size
  const scale = 0.0015 * (800 / Math.max(containerWidth, 1));
  const wx = fbm(x * scale, y * scale + time * 0.3, 3) * config.turbulence;
  const wy = fbm(x * scale + 100, y * scale + time * 0.3 + 100, 3) * config.turbulence;
  // Gust: a wave that sweeps through
  const gustPhase = Math.sin(time * config.gustFreq + x * 0.003) * 0.5 + 0.5;
  const gustPow = gustPhase * gustPhase * gustPhase; // sharpen the gust peaks
  const gustX = config.baseWind[0] * (1 + gustPow * config.gustStrength);
  const gustY = config.baseWind[1] * (1 + gustPow * config.gustStrength * 0.3);
  _windOut.x = gustX + wx;
  _windOut.y = gustY + wy;
  return _windOut;
}

// Wind config as a function of a 0..1 windStrength. Shared so every asset that
// passes the same windStrength gets identical gust frequency / direction /
// turbulence — the deterministic gust wave then sweeps across all of them in
// step (a gust that accelerates the leaves also kicks the sun's rays).
export function makeWindConfig(windStrength) {
  const s = windStrength;
  return {
    baseWind: [60 + s * 160, 8 + s * 20],
    turbulence: 30 + s * 80,
    gustFreq: 0.4 + s * 0.8,
    gustStrength: 1.0 + s * 2.5,
  };
}
